import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * Every figure on the LE/CD tabs derives from the fee lines, so changing one
 * makes Comparison, Tolerance and TRID Checks stale. They live in the
 * `disclosuresAPI` slice and RTK tag invalidation does not cross slices, so it
 * has to be dispatched — the mirror of `invalidateFeesOnSuccess` in
 * `disclosures.js`, which was only ever built for the other direction. Without
 * it an edited fee shows on the worksheet and nowhere else until a reload.
 *
 * **Debounced, because the fan-out is wide.** One `['Disclosure']` invalidation
 * refetches every subscribed query in that slice — on the worksheet tab, the
 * shell's `getOverview` plus the ledger and tolerance reads this page holds
 * open — and they are not cheap. Editing several fees in a row would fire that
 * per field. Collapsing a burst into one refresh a moment after the last edit
 * costs a sub-second lag on tabs nobody is looking at mid-edit.
 *
 * (This once counted four strip queries; they were since consolidated into the
 * single `getOverview` call in `lecd/index.jsx`. The debounce still earns its
 * keep, the arithmetic was just out of date.)
 *
 * Imported lazily on purpose: `disclosures.js` imports this module, and a
 * static import back would be a cycle evaluated while both `createApi` calls
 * are still initialising.
 */
const DISCLOSURE_REFRESH_DEBOUNCE_MS = 600;
let disclosureRefreshTimer = null;

const invalidateDisclosuresOnSuccess = async (_arg, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled;
  } catch {
    // The mutation failed, so the disclosure figures are still accurate.
    return;
  }

  if (disclosureRefreshTimer) clearTimeout(disclosureRefreshTimer);
  disclosureRefreshTimer = setTimeout(async () => {
    disclosureRefreshTimer = null;
    const { disclosuresAPI } = await import('./disclosures');
    dispatch(disclosuresAPI.util.invalidateTags(['Disclosure']));
  }, DISCLOSURE_REFRESH_DEBOUNCE_MS);
};

export const adminFeesworksheetAPI = createApi({
  reducerPath: 'Feesworksheet',
  tagTypes: ['getFees'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    //to get fees worksheet data
    getUserFeesWorksheet: builder.query({
      query: ({ application_Id }) => {
        return {
          url: `b2b/loan/fees/${application_Id}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getFees'],
    }),
    getFeesWorksheet: builder.query({
      query: ({ applicationId }) => {
        const url = `/b2b/loan/applications/${applicationId}/fees-worksheet`;
        return {
          url,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getFees'],
    }),
    getFeesWorksheetTotals: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/totals`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getFees'],
    }),
    //to post and save fees worksheet data
    saveUserFeesWorksheet: builder.mutation({
      query: ({ data, applicationId }) => {
        return {
          url: `b2b/loan/fees/${applicationId}`,
          method: 'POST',
          headers: getHeaders(),
          body: data,
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),
    //to update fees work sheet data
    updateUserFeesWorksheet: builder.mutation({
      query: ({ data, applicationId }) => {
        return {
          url: `b2b/loan/fees/${applicationId}`,
          method: 'PUT',
          headers: getHeaders(),
          body: data,
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),
    addManualFee: builder.mutation({
      query: ({ applicationId, ...body }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/line-items`,
          method: 'POST',
          headers: getHeaders(),
          body,
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),
    updateLineItem: builder.mutation({
      query: ({ applicationId, itemId, ...body }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/line-items/${itemId}`,
          method: 'PATCH',
          headers: getHeaders(),
          body,
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),
    /**
     * Every edit from one press of Save, as one request.
     *
     * The worksheet holds edits and writes them on demand. Per row this was a
     * round trip each, and each one returns the whole recomputed worksheet and
     * invalidates the disclosure figures behind it — so two edited rows meant
     * two full recomputations and two cascades of refetches. The server applies
     * these in a single transaction, so one response is the whole result.
     */
    updateLineItems: builder.mutation({
      query: ({ applicationId, updates }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/line-items`,
          method: 'PATCH',
          headers: getHeaders(),
          body: { updates },
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),
    /**
     * Whether the borrower shopped from the written list, per Section C line.
     *
     * Its own mutation rather than a field on `updateLineItem`, mirroring the
     * server: that path marks the row's *amount* as hand-entered and stops the
     * next pricing refresh restating it. This answers a question about the
     * charge without claiming anything about its figure.
     */
    updateWrittenList: builder.mutation({
      query: ({ applicationId, items }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/line-items/written-list`,
          method: 'PATCH',
          headers: getHeaders(),
          body: { items },
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),
    deleteLineItem: builder.mutation({
      query: ({ applicationId, itemId }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/line-items/${itemId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),
    getLoanCostTypes: builder.query({
      query: () => {
        return {
          url: `b2b/loan/fees/loan_cost_types`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    getLoanAndRate: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `b2b/loans/product/rate_and_loan_amount/application/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getSelectedPricingQuote: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/pricing/selected-quote`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getFees'],
    }),
    getLoadstarData: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `b2b/loan/fees/closing_cost_calculation/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    // get lender
    getRateVendors: builder.query({
      query: () => {
        return {
          url: `b2b/loan/product/rate_vendors`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    updateFeesFromSettleWise: builder.mutation({
      query: ({ application_id }) => {
        return {
          url: `b2b/loan/fees/update_fees_from_settlewise/${application_id}`,
          method: 'PUT',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),

    // New Backend V2 Autofill Endpoints
    autofillFromPricing: builder.mutation({
      query: ({ applicationId, data, pricing_request_id, quote_id }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/autofill/pricing`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            fee_summary: data,
            pricing_request_id,
            quote_id,
            lender_name: data.lender_name,
          },
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),
    syncPricing: builder.mutation({
      query: ({ applicationId, pricing_request_id, quote_id }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/sync-pricing`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            pricing_request_id,
            quote_id,
          },
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),

    autofillFromLodestar: builder.mutation({
      query: ({ applicationId, quote, pricing_request_id, quote_id }) => {
        const normalizedQuote = quote?.quote ?? quote;
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/autofill/lodestar`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            quote: normalizedQuote,
            pricing_request_id,
            quote_id,
          },
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),

    autofillPrepaids: builder.mutation({
      query: ({ applicationId, data, pricing_request_id, quote_id }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/autofill/prepaids`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            ...data,
            pricing_request_id,
            quote_id,
          },
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),

    runLodestarClosingCosts: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `/b2b/integrations/lodestar/closing-costs`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            loan_application_id: applicationId,
            ...data,
          },
        };
      },
      // Writes fees like its siblings do, so it has to invalidate like them.
      // Without this the LE/CD tabs and the Contacts modal kept showing
      // pre-Lodestar figures until something unrelated refetched.
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),

    refreshAllFees: builder.mutation({
      query: ({ applicationId, pricing_request_id, quote_id, prepaids }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/refresh-all`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            pricing_request_id,
            quote_id,
            prepaids,
          },
        };
      },
      invalidatesTags: ['getFees'],
      onQueryStarted: invalidateDisclosuresOnSuccess,
    }),

    getComplianceCheck: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/applications/${applicationId}/fees-worksheet/compliance`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
    }),

  }),
});

export const {
  useGetUserFeesWorksheetQuery,
  useGetFeesWorksheetQuery,
  useGetFeesWorksheetTotalsQuery,
  useSaveUserFeesWorksheetMutation,
  useUpdateUserFeesWorksheetMutation,
  useGetLoanCostTypesQuery,
  useGetLoanAndRateQuery,
  useGetSelectedPricingQuoteQuery,
  useGetLoadstarDataQuery,
  useGetRateVendorsQuery,
  useUpdateFeesFromSettleWiseMutation,
  useAutofillFromPricingMutation,
  useSyncPricingMutation,
  useAutofillFromLodestarMutation,
  useAutofillPrepaidsMutation,
  useAddManualFeeMutation,
  useUpdateLineItemMutation,
  useUpdateLineItemsMutation,
  useUpdateWrittenListMutation,
  useDeleteLineItemMutation,
  useRunLodestarClosingCostsMutation,
  useRefreshAllFeesMutation,
  useLazyGetComplianceCheckQuery,
} = adminFeesworksheetAPI;
